/**
 * SECIMS Map Viewer - Main entry point
 */
import 'ol/ol.css';
import '../css/styles.css';

import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import { defaults as defaultControls } from 'ol/control/defaults';
import ScaleLine from 'ol/control/ScaleLine';
import { defaults as defaultInteractions } from 'ol/interaction/defaults';
import { fromLonLat, transformExtent } from 'ol/proj';

import { allLayers, legendConfig, BASEMAP_ATTRIBUTION } from './layers';
import MeasurementTool from './MeasurementTool';
import LegendControl from './LegendControl';
import MapKey from './MapKey';
import DynamicAttribution from './DynamicAttribution';
import { fetchCatalogLayers, fetchCatalogServices } from './catalog-client';

// ── Map initialisation ─────────────────────────────────────────────

const map = new Map({
  target: 'map',
  // Replace OL's stock Attribution (which would lock to whatever each
  // source declared at construction) with DynamicAttribution, so the
  // panel reflects the publishers of the currently-visible layers.
  controls: defaultControls({
    rotate: false,
    attribution: false
  }).extend([
    new ScaleLine({
      units: 'metric'
    }),
    new DynamicAttribution({ alwaysShownHtml: BASEMAP_ATTRIBUTION })
  ]),
  interactions: defaultInteractions({
    altShiftDragRotate: true,
    pinchRotate: true,
    doubleClickZoom: false
  }),
  layers: allLayers,
  view: new View({
    center: fromLonLat([-60.94115, 13.85741]),
    zoom: 11,
    minZoom: 9,
    extent: transformExtent([-61.8, 13.2, -60.4, 14.5], 'EPSG:4326', 'EPSG:3857')
  })
});

// ── Loading splash dismissal ──────────────────────────────────────
// Two dismissal paths, both idempotent (querySelector returns null after
// removal so re-entry is a no-op):
//   1. Happy path — OL's `rendercomplete` fires after the base tiles
//      finish loading. once() self-unbinds, so later pan/zoom re-renders
//      don't re-trigger.
//   2. Fallback — a 10s timeout force-dismisses the splash even if
//      `rendercomplete` never fires (e.g. zero connectivity to OSM, tile
//      provider hard-failing). User would see an empty map after but at
//      least isn't stuck staring at a spinner forever.
const dismissLoadingSplash = () => {
  const loading = document.getElementById('map-loading');
  if (!loading) return;
  loading.classList.add('hidden');
  setTimeout(() => loading.remove(), 300);
};
map.once('rendercomplete', dismissLoadingSplash);
setTimeout(dismissLoadingSplash, 10000);

// ── Zoom level display ─────────────────────────────────────────────

const zoomLevel = document.getElementById('zoom-level');
const view = map.getView();

function updateZoomLevel() {
  const zoom = Math.round(view.getZoom());
  zoomLevel.textContent = 'Zoom: ' + zoom;
}

view.on('change:resolution', updateZoomLevel);
updateZoomLevel();

// ── Compass rotation control ───────────────────────────────────────

const compass = document.getElementById('compass');
const needle = document.getElementById('compass-needle');
let dragging = false;
let didDrag = false;

function updateNeedle() {
  const rotation = view.getRotation();
  const deg = rotation * 180 / Math.PI;
  needle.style.transform = 'rotate(' + deg + 'deg)';
}

function getAngle(e) {
  const rect = compass.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const x = (e.clientX || e.touches[0].clientX) - cx;
  const y = (e.clientY || e.touches[0].clientY) - cy;
  return Math.atan2(x, -y);
}

let startAngle = 0;
let startRotation = 0;

function onDragStart(e) {
  e.preventDefault();
  dragging = true;
  didDrag = false;
  startAngle = getAngle(e);
  startRotation = view.getRotation();
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
  document.addEventListener('touchmove', onDragMove);
  document.addEventListener('touchend', onDragEnd);
}

function onDragMove(e) {
  if (!dragging) return;
  didDrag = true;
  const currentAngle = getAngle(e);
  const delta = currentAngle - startAngle;
  view.setRotation(startRotation + delta);
}

function onDragEnd() {
  dragging = false;
  document.removeEventListener('mousemove', onDragMove);
  document.removeEventListener('mouseup', onDragEnd);
  document.removeEventListener('touchmove', onDragMove);
  document.removeEventListener('touchend', onDragEnd);
}

compass.addEventListener('click', function () {
  if (!didDrag) {
    view.animate({ rotation: 0, duration: 250 });
  }
});

compass.addEventListener('mousedown', onDragStart);
compass.addEventListener('touchstart', onDragStart);

view.on('change:rotation', updateNeedle);
updateNeedle();

// ── Measurement tool ───────────────────────────────────────────────

const measureTool = new MeasurementTool(map, {
  tooltipClass: 'measure-tooltip'
});

// ── Legend control with catalog layer discovery ────────────────────

const mapKey = new MapKey(map);

// Held module-level so the singleclick handler can call into it for
// WMS GetFeatureInfo. May be null until initLegend() resolves; the
// click handler null-checks accordingly.
let legendControl = null;

async function initLegend() {
  const [catalogLayers, catalogServices] = await Promise.all([
    fetchCatalogLayers(),
    fetchCatalogServices()
  ]);
  console.log(
    `Discovered ${catalogLayers.length} dataset layer(s) and ` +
    `${catalogServices.length} web service link(s) from catalog`
  );
  legendControl = new LegendControl(
    map, legendConfig, catalogLayers, mapKey, catalogServices
  );

  // Deep-link from the landing-page search bar: ?layer=<record_id>
  // toggles that layer on and opens its metadata modal. Handled here
  // (rather than inside LegendControl) so the URL contract stays
  // colocated with the other top-level map wiring.
  const layerParam = new URLSearchParams(window.location.search).get('layer');
  if (layerParam) {
    legendControl.activateLayerById(layerParam);
  }
}

initLegend();

// ── Feature popup on click ────────────────────────────────────────

const popupContainer = document.getElementById('popup');
const popupContent = document.getElementById('popup-content');
const popupCloser = document.getElementById('popup-closer');

const popupOverlay = new Overlay({
  element: popupContainer,
  autoPan: { animation: { duration: 250 } },
  positioning: 'bottom-center',
  offset: [0, -12]
});
map.addOverlay(popupOverlay);

popupCloser.addEventListener('click', function (e) {
  e.preventDefault();
  popupOverlay.setPosition(undefined);
  popupCloser.blur();
});

// Keys to exclude from the attributes table
const EXCLUDED_KEYS = new Set([
  'geometry', 'geom', 'the_geom', 'shape',
  'layer', 'mvtgeom', '_sa_instance_state'
]);

// ── KoboToolbox attachment metadata ────────────────────────────────
// Dict of collection_id → { source_id, attachments } where
// ``attachments`` is itself a dict of column_name → attachment-meta.
// Populated once at load by GET /api/kobo/public/collections so the
// popup can render image / audio / video columns inline instead of
// showing the raw filename. Lookup is cheap; we keep the dict alive
// for the page lifetime.
//
// IMPORTANT: ``Map`` at module scope is the OpenLayers Map class
// (see ``import Map from 'ol/Map'`` at the top of this file), NOT
// the JavaScript built-in. Using ``new Map()`` here would build an
// OL map with no ``.set()`` / ``.get()`` / ``.has()`` methods. Plain
// dictionaries are simpler and dodge the shadow entirely.
const koboCollectionMeta = Object.create(null);
const PYCSW_BASE = (window.PYCSW_URL || window.location.origin)
                     .replace(/\/$/, '');

async function loadKoboCollectionMeta() {
  try {
    const resp = await fetch(`${PYCSW_BASE}/api/kobo/public/collections`,
                             { credentials: 'omit' });
    if (!resp.ok) return;
    const data = await resp.json();
    for (const c of (data.collections || [])) {
      if (!c.collection_id) continue;
      const attachments = Object.create(null);
      for (const a of (c.attachment_columns || [])) {
        if (a && a.column_name) attachments[a.column_name] = a;
      }
      const entry = {
        source_id: c.source_id,
        record_id: c.record_id,
        collection_id: c.collection_id,
        attachments,
      };
      // Layers in the SEC-IMS Map Viewer are tagged with the pycsw
      // record identifier via ``layer.get('catalogId')`` — that's
      // the key the popup looks up. Index this entry by both the
      // record_id AND the pygeoapi collection_id so a click on
      // either flavour of catalogId resolves.
      if (c.record_id) koboCollectionMeta[c.record_id] = entry;
      koboCollectionMeta[c.collection_id] = entry;
    }
  } catch (err) {
    console.warn('Could not load Kobo collection metadata:', err);
  }
}
loadKoboCollectionMeta();

// Debug aid: expose the metadata and the URL helpers on a window
// namespace so an operator can inspect them from the console without
// re-bundling. Module-scoped const isn't reachable otherwise.
if (typeof window !== 'undefined') {
  window.__kobo = {
    meta: koboCollectionMeta,
    base: PYCSW_BASE,
    reload: loadKoboCollectionMeta,
  };
}

function koboDownloadUrl(sourceId, koboUuid, filename) {
  return `${PYCSW_BASE}/api/kobo/download/`
    + `${encodeURIComponent(sourceId)}/`
    + `${encodeURIComponent(koboUuid)}/`
    + `${encodeURIComponent(filename)}`;
}

function renderKoboAttachment(baseType, url, filename) {
  // Returns an HTML string that the popup table will inject. Each
  // variant wraps the media in a link to the same URL so clicking
  // through gives the user the full file in a new tab.
  const safeUrl = escPopup(url);
  const safeName = escPopup(filename);
  if (baseType === 'image') {
    return `<a href="${safeUrl}" target="_blank" rel="noopener">`
         + `<img src="${safeUrl}" alt="${safeName}" `
         + `style="max-width:240px;max-height:160px;display:block;"></a>`;
  }
  if (baseType === 'audio' || baseType === 'background-audio') {
    return `<audio controls preload="none" src="${safeUrl}" `
         + `style="max-width:240px;"></audio>`
         + `<br><a href="${safeUrl}" target="_blank" rel="noopener" `
         + `style="font-size:.75rem;">${safeName}</a>`;
  }
  if (baseType === 'video') {
    return `<video controls preload="metadata" src="${safeUrl}" `
         + `style="max-width:240px;max-height:160px;"></video>`
         + `<br><a href="${safeUrl}" target="_blank" rel="noopener" `
         + `style="font-size:.75rem;">${safeName}</a>`;
  }
  // file / unknown — render a download link only.
  return `<a href="${safeUrl}" target="_blank" rel="noopener">`
       + `${safeName}</a>`;
}

map.on('singleclick', async function (evt) {
  // Don't show popups when the measurement tool is active
  const measureControl = document.getElementById('measure-control');
  if (measureControl && measureControl.classList.contains('active')) return;

  // Collect all features at click point, grouped by layer
  const hits = [];
  const seenLayers = new Set();

  map.forEachFeatureAtPixel(evt.pixel, function (feature, layer) {
    if (!layer || !layer.getVisible()) return;

    const title = layer.get('title') || '';
    const catalogId = layer.get('catalogId');
    if (!catalogId && !title) return;

    const props = feature.getProperties();
    const entries = Object.entries(props).filter(
      ([k]) => !EXCLUDED_KEYS.has(k.toLowerCase())
    );
    if (entries.length === 0) return;

    const layerName = title || catalogId || 'Feature';
    // One entry per layer (first feature per layer at this pixel)
    const layerKey = catalogId || title;
    if (seenLayers.has(layerKey)) return;
    seenLayers.add(layerKey);

    hits.push({ layerName, entries, catalogId, props });
  });

  // WMS GetFeatureInfo for visible web-service layers. Awaited so the
  // popup renders once with all tabs rather than flicker as remote
  // responses trickle in. Failures are logged and dropped inside
  // getWebServiceInfoAt so a slow/broken endpoint can't block the
  // vector popup.
  if (legendControl) {
    const view = map.getView();
    const wmsHits = await legendControl.getWebServiceInfoAt(
      evt.coordinate, view.getResolution(), view.getProjection()
    );
    for (const wmsHit of wmsHits) {
      const entries = Object.entries(wmsHit.properties).filter(
        ([k, v]) => !EXCLUDED_KEYS.has(k.toLowerCase()) &&
                    v !== null && v !== undefined && v !== ''
      );
      if (entries.length === 0) continue;
      hits.push({ layerName: wmsHit.layerName, entries });
    }
  }

  if (hits.length === 0) {
    popupOverlay.setPosition(undefined);
    return;
  }

  let html = '';

  if (hits.length === 1) {
    // Single layer — no tabs needed
    html = '<div class="popup-title">' + escPopup(hits[0].layerName) + '</div>';
    html += buildAttrTable(hits[0].entries, hits[0].catalogId, hits[0].props);
  } else {
    // Multiple layers — tabbed interface
    html = '<div class="popup-tabs">';
    hits.forEach(function (h, i) {
      html += '<button class="popup-tab' + (i === 0 ? ' active' : '') +
              '" data-tab="' + i + '">' + escPopup(h.layerName) + '</button>';
    });
    html += '</div>';

    hits.forEach(function (h, i) {
      html += '<div class="popup-tab-panel' + (i === 0 ? ' active' : '') +
              '" data-panel="' + i + '">';
      html += buildAttrTable(h.entries, h.catalogId, h.props);
      html += '</div>';
    });
  }

  popupContent.innerHTML = html;

  // Bind tab clicks
  popupContent.querySelectorAll('.popup-tab').forEach(function (tab) {
    tab.addEventListener('click', function () {
      const idx = tab.getAttribute('data-tab');
      popupContent.querySelectorAll('.popup-tab').forEach(function (t) {
        t.classList.toggle('active', t.getAttribute('data-tab') === idx);
      });
      popupContent.querySelectorAll('.popup-tab-panel').forEach(function (p) {
        p.classList.toggle('active', p.getAttribute('data-panel') === idx);
      });
    });
  });

  popupOverlay.setPosition(evt.coordinate);
});

function buildAttrTable(entries, catalogId, props) {
  // Kobo-collection attachment rendering. Two URL sources, in
  // order of preference:
  //   1. A sibling generated column ``<key>_url`` on the same
  //      feature — produced server-side by mapper.py's attachment
  //      DDL (slice 8 polish). Self-describing GeoJSON; works
  //      even for tables we know nothing about client-side.
  //   2. Falling back to the public-collections metadata + URL
  //      construction. Used for sources that haven't had
  //      refresh-schema run on them yet — they still have the
  //      filename column but no ``_url`` sibling.
  // Non-Kobo vector collections and WMS hits fall through to the
  // plain key/value rendering.
  const meta = catalogId ? koboCollectionMeta[catalogId] : null;
  const koboUuid = props && props.kobo_uuid;
  // See koboCollectionMeta docstring for why plain dicts instead of
  // built-in Map: the OL Map import shadows it at module scope.
  const propsByKey = Object.create(null);
  for (const [k, v] of entries) propsByKey[k] = v;

  let html = '<div class="popup-table-wrap"><table class="popup-table">';
  for (const [key, value] of entries) {
    if (value === null || value === undefined || value === '') continue;

    // Skip the ``_url`` sibling rows themselves — they're rendered
    // alongside their attachment partner, not as their own row.
    if (key.endsWith('_url')
        && Object.prototype.hasOwnProperty.call(propsByKey, key.slice(0, -4))) {
      continue;
    }

    const att = meta && meta.attachments && meta.attachments[key];
    let renderedAttachment = null;

    if (att) {
      // Prefer the server-emitted sibling URL when present. The
      // server emits a relative path (``/api/kobo/download/...``)
      // for portability across deployments; we prepend PYCSW_BASE
      // so the URL points at the pycsw host even when the map
      // viewer is served from a different origin.
      const siblingUrl = propsByKey[key + '_url'];
      if (siblingUrl) {
        const fullUrl = String(siblingUrl).startsWith('http')
          ? String(siblingUrl)
          : PYCSW_BASE + String(siblingUrl);
        renderedAttachment = renderKoboAttachment(
          att.base_type, fullUrl, String(value));
      } else if (koboUuid && meta.source_id) {
        // Fallback for tables predating the sibling-URL change.
        const url = koboDownloadUrl(
          meta.source_id, koboUuid, String(value));
        renderedAttachment = renderKoboAttachment(
          att.base_type, url, String(value));
      }
    }

    if (renderedAttachment) {
      html += '<tr><td class="popup-key">' + escPopup(key) +
              '</td><td class="popup-val">' + renderedAttachment +
              '</td></tr>';
    } else {
      html += '<tr><td class="popup-key">' + escPopup(key) +
              '</td><td class="popup-val">' + escPopup(String(value)) + '</td></tr>';
    }
  }
  html += '</table></div>';
  return html;
}

// Change cursor on hover over features
map.on('pointermove', function (evt) {
  if (evt.dragging) return;
  const pixel = map.getEventPixel(evt.originalEvent);
  const hit = map.hasFeatureAtPixel(pixel, {
    layerFilter: function (layer) {
      return !!layer.get('catalogId');
    }
  });
  map.getTargetElement().style.cursor = hit ? 'pointer' : '';
});

function escPopup(str) {
  const d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}
